import { FormControlLabel, Switch, Tooltip, IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DownloadButtons from "./Downloads";
import { canUseLogScale } from "../../utils/charts";

export default function IndicatorControls({
    info,
    item_code,
    data,
    showDataLabels,
    showSubregions,
    useLogScale,
    toggleDataLabels,
    toggleSubregions,
    toggleLogScale,
    originalData,
    refDownload,
    title,
    country,
    params,
    currentTab
}) {
    // Helper to decide if the Subregion toggle should be visible
    const canShowSubregionToggle = () => {
        const hasMultipleCombos = (
            Array.isArray(info?.item_code) && info.item_code.length > 1
        ) || (
            Array.isArray(info?.element_code) && info.element_code.length > 1
        ) || (
            Array.isArray(info?.indicator_code) && info.indicator_code.length > 1
        );

        const hasSubregionalSeries = () => {
            if (info?.chart_type === "line") {
                return (data?.datasets?.length ?? 0) > 1;
            }
            if (info?.chart_type === "multipleAxis") {
                return (data?.compareDatasets?.length ?? 0) > 0;
            }
            return false;
        };

        if (info?.exclude_subregional_comparison) return false;

        if (info?.chart_type === "line") {
            if (hasMultipleCombos) return false;
            return hasSubregionalSeries();
        }

        if (info?.chart_type === "multipleAxis") {
            return hasSubregionalSeries();
        }

        return false;
    };
    // Offered only where it earns its place: a line chart whose series span orders of
    // magnitude, with no zero or negative value that a log axis could not plot.
    // Not offered where the indicator already declares a dual axis: the two are answers to
    // the same problem, and switching to a single log axis undoes the split.
    const canShowLogScaleToggle = () =>
        info?.chart_type === "line" && !info?.dual_axis && canUseLogScale(data);

    return (
        <>
            {canShowLogScaleToggle() && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={Boolean(useLogScale)}
                            size="small"
                            onChange={toggleLogScale}
                        />
                    }
                    label="Log scale"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
                />
            )}
            {canShowSubregionToggle() && (
                <FormControlLabel
                    control={
                        <Switch
                            defaultChecked={info.chart_type === "multipleAxis" ? false : showSubregions ? false : true}
                            size="small"
                            onClick={toggleSubregions}
                        />
                    }
                    label="Subregion values"
                />
            )}
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        onClick={toggleDataLabels}
                        defaultChecked={showDataLabels ? true : false}
                    />
                }
                label="Data labels"
            />
            <Tooltip
                title={
                    <div>
                        <div>• Use the slider below the chart to zoom Y-axis vertically.</div>
                        <div>• Click and drag over data points to zoom X-axis horizontally.</div>
                        <div>• Zoom reset in the bottom right corner.</div>
                    </div>
                }
                placement="top"
                arrow
            >
                <IconButton
                    size="small"
                    sx={{
                        ml: 0.5,
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main'
                        }
                    }}
                >
                    <HelpOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <DownloadButtons
                originalData={originalData}
                ref={refDownload}
                title={title}
                country={country}
                params={params}
                data={data}
                currentTab={currentTab}
            />
        </>
    );
}
